import React, { useEffect, useState } from "react";
import BackdropComponent from "../../components/Spinner/Spinner";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { getPiiData } from "../../services/userServices";
import SnackBar from "components/SnackBar/SnackBar";
import { useTranslation } from "react-i18next";
import { Box, Grid, Paper, Tooltip, Typography } from "@mui/material";
import { fontStyles } from "components/Common/commonStyles";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import { ContentCopy } from "@mui/icons-material";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { format } from "date-fns";
import { useAuth } from "context/AuthContext";
import piiAuditLog from "../../utils/piiAuditLog";

const contentFontStyle = {
  ...fontStyles,
  fontSize: 14,
  letterSpacing: 0.1,
};

// ASTT-7304: PII Access Logging. Extracted so the audit log call can report
// the same field set used for the clipboard export, rather than duplicating
// this list a second time.
const PII_REPORT_COLUMNS = [
  "Oracle ID",
  "Class Roster ID",
  "Project Code",
  "Site Code",
  "Parent Wave",
  "Wave",
  "Candidate ID",
  "Oracle User Name",
  "TTEC E-Mail",
  "Personal E-Mail",
  "Mobile Phone Number",
  "First Name",
  "Middle Initial",
  "Last Name",
  "Date of Birth",
  "Gender",
  "Home_Phone",
  "Address",
  "Address #2",
  "City",
  "Region",
  "State",
  "Zip Post Code",
  "Country Code",
  "Site",
  "Location ID",
  "CPP Code",
  "Client Program",
  "Department",
  "Department Code",
  "Job Title",
  "Trainer Oracle ID",
  "Trainer Name",
  "Trainer E-Mail",
  "Bilingual",
  "Operational Team Lead Employee Number",
  "Operational Team Lead",
  "Operational Team Lead E-Mail",
  "TCI Zoom Meeting Link",
  "TCI zoom password",
  "TTF Zoom Meeting Link",
  "TTF zoom password",
  "Tech Test zoom link",
  "Tech Test zoom password",
  "Product training zoom link",
  "Product training zoom password",
  "TCI Recovery zoom link",
  "TCI Recovery zoom password",
  "Tech Test Recovery zoom link",
  "Tech Test Recovery zoom pwd",
  "Stage Tech Check (Bot)",
  "TCI Date",
  "TCI Time",
  "TCI Recovery Date",
  "TCI Recovery Time",
  "Worked Hours TCI",
  "WorkedHours TCI Recovery",
  "TPC Date",
  "TPC Time",
  "Tech Test Date",
  "Tech Test Time",
  "Tech Test Recovery Date",
  "Tech Test Recovery Time",
  "TTF Date",
  "TTF Time",
  "Product Trng Date",
  "Product Trng Time",
  "Tech Equipment Solution",
  "Nesting Start Date",
  "Nesting Start Time",
  "Nesting End Date",
  "Nesting End Time",
  "NestingLocation",
  "NestingLeader",
  "Production Date",
  "ACC Date",
  "ACC Time",
  "SRW Download Due Date",
  "SRW Download Due Time",
  "Worked Hours TechTest",
  "Worked Hours Tech Test Recovery",
  "Production Classification",
  "Training Classification",
  "ASTT Trainee Status",
  "Term Date",
  "Hire Date",
  "Term Reason",
  "Oracle Trainee Status",
  "TrackingNumber",
  "EquipmentTrackingNotes",
  "PONumber",
  "CorpShipDate",
  "Reference",
  "CurrentEquipmentSetup",
  "Current Oracle SuperVisor",
  "TTECRegistrationStatus",
  "TraineeAsset",
  "AgentComments",
  "NPN",
  "License Number",
  "Trainee Added Date",
  "Trainee Added By",
];

// ASTT-7304: PII Access Logging — derived from the actual masking logic in
// [dbo].[EmployeePIIReport] (confirmed by reading the SP directly).
//
// - "Date of Birth" is commented out in the SP's SELECT and is never actually
//   returned, despite existing as a table header here — excluded from the
//   audit "shown" list so the log doesn't claim a field that was never sent.
// - PII_Group_FULL / PII_Group_Med: nothing masked.
// - PII_Group_Low: exactly these 7 fields are replaced with 'RESTRICTED'.
// - KNOWN GAP (accepted): the SP has three additional override paths — the
//   roster's OG contact, the roster's Trainer, and a >1-nesting-leader
//   scenario — that can further restrict or unrestrict fields independently
//   of @PIIAccess, based on @LoginUserEmail/@classRosterId context the
//   frontend has no visibility into. This logging does not attempt to
//   detect those cases; it reflects the tier-only rule.
const PII_FIELDS_NOT_RETURNED_BY_SP = ["Date of Birth"];
const PII_LOW_TIER_MASKED_FIELDS = [
  "Personal E-Mail",
  "Mobile Phone Number",
  "Gender",
  "Home_Phone",
  "Address",
  "Address #2",
  "Zip Post Code",
];

function PiiDisplay(props) {
  const [loading, setLoader] = useState([false]);
  const { roles } = useAuth();

  const [PiiData, setPiiData] = useState([]);
  const [order, setOrder] = useState("ASC");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  let piiAccess;
  let setPiiAccess = (accessRole) => {
    if (accessRole.includes("PII_Group_FULL")) {
      piiAccess = "PII_Group_FULL";
    } else if (accessRole.includes("PII_Group_Med")) {
      piiAccess = "PII_Group_Med";
    } else if (
      accessRole.includes("PII_Group_Low") ||
      accessRole.includes("PII_Group")
    ) {
      piiAccess = "PII_Group_Low";
    } else {
      piiAccess = "None";
    }
  };

  const { t } = useTranslation(["piiReport"]);

  useEffect(() => {
    let cancelled = false;

    localStorage.setItem("ContactRoster", window.location.pathname);

    const fetchData = async () => {
      if (!cancelled) {
        setLoader(true);
        setPiiAccess(roles);
      }

      try {
        const piiDetails = await getPiiData(props.rosterId, piiAccess);

        if (cancelled) return;

        if (piiDetails.status === true) {
          window.localStorage.removeItem("ContactRoster");

          const sorted = piiDetails.data.sort((a, b) =>
            a["Last Name"]
              ? b["Last Name"]
                ? a["Last Name"].localeCompare(b["Last Name"])
                : -1
              : 1,
          );

          if (!cancelled) {
            setPiiData(sorted);
          }

          // ASTT-7304: PII Access Logging. Only log when PII was actually shown.
          if (sorted.length > 0) {
            const oracleIds = sorted
              .map((row) => row["Oracle ID"])
              .filter((id) => id !== null && id !== undefined && id !== "")
              .join(",");

            if (oracleIds) {
              const auditableColumns = PII_REPORT_COLUMNS.filter(
                (col) => !PII_FIELDS_NOT_RETURNED_BY_SP.includes(col),
              );

              const maskedFields =
                piiAccess === "PII_Group_Low" ? PII_LOW_TIER_MASKED_FIELDS : [];

              const shownFields = auditableColumns.filter(
                (col) => !maskedFields.includes(col),
              );

              piiAuditLog({
                oracleIds,
                userGroup: piiAccess,
                sourceModule: "ContactRoster",
                moduleReference: props.rosterId,
                piiFieldsShown: shownFields.join(","),
                piiFieldsMasked: maskedFields.join(","),
              });
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch PII data:", error);
        }
      } finally {
        if (!cancelled) {
          setLoader(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const sorting = (col) => {
    let sorted;
    if (order === "ASC") {
      sorted = [...PiiData].sort((a, b) => (a[col] > b[col] ? 1 : -1));
      setPiiData(sorted);
      setOrder("DSC");
    }
    if (order === "DSC") {
      sorted = [...PiiData].sort((a, b) => (a[col] < b[col] ? 1 : -1));
      setPiiData(sorted);
      setOrder("ASC");
    }
  };

  const copyToClipboard = async () => {
    try {
      // Define the column headers in the same order as they appear in the table
      const headers = PII_REPORT_COLUMNS;

      // Helper function to escape and format cell data for CSV/TSV
      const formatCellData = (value) => {
        if (value === null || value === undefined) {
          return "";
        }

        // Convert to string
        let cellValue = String(value);

        // Handle boolean values
        if (typeof value === "boolean") {
          cellValue = value ? "Yes" : "No";
        }

        // Escape quotes and wrap in quotes if contains comma, tab, newline, or quote
        if (cellValue.includes('"')) {
          cellValue = cellValue.replace(/"/g, '""');
        }

        if (
          cellValue.includes("\t") ||
          cellValue.includes("\n") ||
          cellValue.includes("\r") ||
          cellValue.includes('"')
        ) {
          cellValue = `"${cellValue}"`;
        }

        return cellValue;
      };

      // Create the data rows
      const dataRows = PiiData.map((row) => {
        return headers.map((header) => {
          let value = row[header];

          // Special handling for TTECRegistrationStatus
          if (header === "TTECRegistrationStatus") {
            value = row.TTECRegistrationStatus ? "Yes" : "No";
          }

          // Special handling for Trainee Added Date
          if (header === "Trainee Added Date") {
            value = row.RosterAddDate;
          }

          // Special handling for Trainee Added By
          if (header === "Trainee Added By") {
            value = row.CreatedBy;
          }

          return formatCellData(value);
        });
      });

      // Combine headers and data
      const allRows = [
        headers.map(formatCellData), // Header row
        ...dataRows, // Data rows
      ];

      // Convert to tab-separated values (TSV) - Excel handles this better than CSV
      const tsvContent = allRows.map((row) => row.join("\t")).join("\n");

      // Try to use the modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tsvContent);

        setSnack({
          open: true,
          message: "Copied!",
          severity: "success",
        });
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = tsvContent;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setSnack({
          open: true,
          message: "Copied!",
          severity: "success",
        });
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Failed to copy data",
        severity: "error",
      });
      console.error("Failed to copy data to clipboard:", error);
    }
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box sx={fontStyles}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <CustomButton
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#3299FF",
                  color: "#FFF",
                  borderRadius: 0,
                }}
                endIcon={<ContentCopy />}
                onClick={copyToClipboard}
              >
                {t("copyData")}
              </CustomButton>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                id="piiTable"
                sx={{ height: "calc(100vh - 35px)", overflowY: "auto" }}
              >
                <Table stickyHeader size="large">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          zIndex: 300,
                        }}
                      >
                        <TextLabel text={t("Oracle ID")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <TextLabel text={t("Class Roster ID")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Project Code")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Site Code")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Parent Wave")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Wave")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Candidate ID")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Oracle User Name")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TTEC E-Mail")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TTEC E-Mail")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Personal E-Mail")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Mobile Phone Number")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("First Name")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("First Name")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Middle Initial")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Middle Initial")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Last Name")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Last Name")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Date of Birth")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Gender")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Home_Phone")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Address")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("Address #2")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("City")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("City")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Region")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Region")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("State")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("State")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Zip Post Code")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Zip Post Code")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Country Code")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Country Code")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Site")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Site")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Location ID")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Location ID")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("CPP Code")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("CPP Code")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Client Program")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Client Program")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Department")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Department")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Department Code")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Department Code")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Job Title")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Job Title")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Trainer Oracle ID")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Trainer Oracle ID")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Trainer Name")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Trainer Name")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Trainer E-Mail")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Trainer E-Mail")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Bilingual")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Bilingual")} />
                      </TableCell>
                      {/* <TableCell onClick={() => sorting("Rehire Flag")}  sx={{ whiteSpace: "nowrap" }} align="left">
                Rehire Flag
              </TableCell>
              <TableCell onClick={() => sorting("Employment Service Status")}  sx={{ whiteSpace: "nowrap" }} align="left">
                Employment Service Status
              </TableCell> */}
                      <TableCell
                        onClick={() =>
                          sorting("Operational Team Lead Employee Number")
                        }
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel
                          text={t("Operational Team Lead Employee Number")}
                        />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Operational Team Lead")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Operational Team Lead")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Operational Team Lead E-Mail")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Operational Team Lead E-Mail")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Zoom Meeting Link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Zoom Meeting Link")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI zoom password")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI zoom password")} />
                      </TableCell>
                      {/* <TableCell onClick={() => sorting("TPC Zoom Meeting Link")} sx={{ whiteSpace: "nowrap" }} align="left">
                TPC Zoom Meeting Link
              </TableCell>
              <TableCell onClick={() => sorting("TPC zoom password")} sx={{ whiteSpace: "nowrap" }} align="left">
                TPC zoom password
              </TableCell> */}
                      <TableCell
                        onClick={() => sorting("TTF Zoom Meeting Link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TTF Zoom Meeting Link")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TTF zoom password")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TTF zoom password")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test zoom link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test zoom link")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test zoom password")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test zoom password")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Product training zoom link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Product training zoom link")} />
                      </TableCell>
                      <TableCell
                        onClick={() =>
                          sorting("Product training zoom password")
                        }
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Product training zoom password")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Recovery zoom link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Recovery zoom link")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Recovery zoom password")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Recovery zoom password")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Recovery zoom link")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Recovery zoom link")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Recovery zoom pwd")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Recovery zoom pwd")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Stage Tech Check (Bot)")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Stage Tech Check (Bot)")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Recovery Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Recovery Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TCI Recovery Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Recovery Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Worked Hours TCI")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Worked Hours")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("WorkedHours TCI Recovery")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TCI Recovery Worked Hours")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TPC Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TPC Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TPC Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TPC Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Recovery Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Recovery Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Test Recovery Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Test Recovery Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TTF Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TTF Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TTF Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("TTF Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Product Trng Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Product Trng Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Product Trng Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Product Trng Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Tech Equipment Solution")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tech Equipment Solution")} />
                      </TableCell>
                      {/* <TableCell  onClick={() => sorting("Tech Equipment Solution Description")} sx={{ whiteSpace: "nowrap" }} align="left">
                Tech Equipment Solution Description
              </TableCell> */}
                      <TableCell
                        onClick={() => sorting("Nesting Start Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting Start Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Nesting Start Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting Start Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Nesting End Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting End Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Nesting End Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting End Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("NestingLocation")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting Location")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("NestingLeader")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Nesting Leader")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Production Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Production Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("ACC Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("ACC Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("ACC Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("ACC Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("SRW Download Due Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("SRW Download Due Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("SRW Download Due Time")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("SRW Download Due Time")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Worked Hours TechTest")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("BOT Worked Hours")} />
                      </TableCell>
                      <TableCell
                        onClick={() =>
                          sorting("Worked Hours Tech Test Recovery")
                        }
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("BOT Recovery Worked Hours")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Production Classification")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Production Classification")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Training Classification")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Training Classification")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("ASTT Trainee Status")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("ASTT Trainee Status")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Term Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Term Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Hire Date")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Hire Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Term Reason")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Term Reason")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Oracle Trainee Status")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Oracle Trainee Status")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TrackingNumber")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Tracking Number")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("EquipmentTrackingNotes")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Equipment Tracking Notes")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("PONumber")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("PO Number")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("CorpShipDate")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("CorpShip Date")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Reference")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Reference")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("CurrentEquipmentSetup")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Current Equipment Setup")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("Current Oracle SuperVisor")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("Current Oracle SuperVisor")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TTECRegistrationStatus")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="right"
                      >
                        <TextLabel text={t("TTEConnect Registration Status")} />
                      </TableCell>
                      <TableCell
                        onClick={() => sorting("TraineeAsset")}
                        sx={{ whiteSpace: "nowrap" }}
                        align="left"
                      >
                        <TextLabel text={t("IT Asset Name")} />
                      </TableCell>
                      <TableCell
                        sx={{ whiteSpace: "nowrap", width: 10 }}
                        align="left"
                      >
                        <TextLabel text={t("Agent comments")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("NPN")} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }} align="left">
                        <TextLabel text={t("License Number")} />
                      </TableCell>
                      <TableCell
                        sx={{ whiteSpace: "nowrap", width: 10 }}
                        align="left"
                      >
                        <TextLabel text={t("Trainee Added Date")} />
                      </TableCell>
                      <TableCell
                        sx={{ whiteSpace: "nowrap", width: 10 }}
                        align="left"
                      >
                        <TextLabel text={t("Trainee Added By")} />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PiiData.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          sx={{
                            ...contentFontStyle,
                            whiteSpace: "nowrap",
                            position: "sticky",
                            left: 0,
                          }}
                          component="th"
                          scope="row"
                          align="right"
                        >
                          {row["Oracle ID"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Class Roster ID"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Project Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Site Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Parent Wave"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.Wave}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Candidate ID"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Oracle User Name"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["TTEC E-Mail"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Personal E-Mail"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Mobile Phone Number"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["First Name"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Middle Initial"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Last Name"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Date of Birth"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Gender}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.Home_Phone}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Address}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Address #2"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.City}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Region}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.State}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Zip Post Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Country Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Site}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Location ID"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["CPP Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Client Program"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Department}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Department Code"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Job Title"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Trainer Oracle ID"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Trainer Name"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Trainer E-Mail"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.Bilingual}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Operational Team Lead Employee Number"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Operational Team Lead"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Operational Team Lead E-Mail"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["TCI Zoom Meeting Link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI zoom password"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["TTF Zoom Meeting Link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TTF zoom password"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Tech Test zoom link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test zoom password"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Product training zoom link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Product training zoom password"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["TCI Recovery zoom link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI Recovery zoom password"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Tech Test Recovery zoom link"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test Recovery zoom pwd"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Stage Tech Check (Bot)"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI Recovery Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TCI Recovery Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Worked Hours TCI"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["WorkedHours TCI Recovery"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TPC Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TPC Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test Recovery Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Test Recovery Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TTF Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["TTF Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Product Trng Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Product Trng Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Tech Equipment Solution"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Nesting Start Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Nesting Start Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Nesting End Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Nesting End Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["NestingLocation"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap " }}
                          align="left"
                        >
                          {row["NestingLeader"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Production Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["ACC Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["ACC Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["SRW Download Due Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["SRW Download Due Time"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Worked Hours TechTest"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Worked Hours Tech Test Recovery"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Production Classification"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Training Classification"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["ASTT Trainee Status"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Term Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Hire Date"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row["Term Reason"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Oracle Trainee Status"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.TrackingNumber}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.EquipmentTrackingNotes}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.PONumber}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.CorpShipDate}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.Reference}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.CurrentEquipmentSetup}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["Current Oracle SuperVisor"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.TTECRegistrationStatus ? "Yes" : "No"}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.TraineeAsset}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...contentFontStyle,
                            maxWidth: 300, // Set maximum width in pixels
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          align="left"
                        >
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxHeight: 200,
                                  overflowY: "auto",
                                  pr: 0.5,
                                  scrollbarWidth: "thin",
                                  scrollbarColor: "rgba(0,0,0,0.3) transparent",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    whiteSpace: "pre-wrap", // Preserves line breaks in tooltip
                                  }}
                                >
                                  {row.AgentComments || "N/A"}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 400,
                                },
                              },
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                              }}
                            >
                              {row.AgentComments || "N/A"}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["NPN"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row["License Number"]}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="right"
                        >
                          {row.RosterAddDate}
                        </TableCell>
                        <TableCell
                          sx={{ ...contentFontStyle, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {row.CreatedBy}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
}

export default PiiDisplay;
